import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getArticleById, articles } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block" />
            <h1 className="font-heading text-xl font-bold text-foreground-800 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该文章可能已被移除或链接无效</p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回作品浏览
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      datePublished: article.date,
      dateModified: article.date,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '水野朝阳官方粉丝站',
        url: 'https://mizunocore.github.io',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://mizunocore.github.io/article/${article.id}`,
      },
    },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`水野朝阳,${article.category},${article.tags.join(',')}`}
        canonicalPath={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
        publishedTime={article.date}
        modifiedTime={article.date}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="px-4 md:px-6 max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '作品浏览', path: '/category/all' },
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" />
                  {article.author}
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <div
                className="w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${article.image})` }}
              />
            </div>

            <div
              className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4 mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-12">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 max-w-6xl mx-auto pb-20">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-200/60 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <div
                        className="w-full h-full bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                        style={{ backgroundImage: `url(${related.image})` }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                          {related.category}
                        </span>
                        <span className="text-xs text-foreground-400">{related.date}</span>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}